import {
  FiHome,
  FiBox,
  FiCreditCard,
  FiPieChart,
  FiFileText,
  FiRepeat,
  FiTrendingUp,
  FiShoppingCart,
  FiDollarSign,
  FiInbox,
  FiUserCheck,
  FiUsers,
  FiBriefcase,
  FiCalendar,
  FiShield,
} from 'react-icons/fi';
import { ROLES, DEPARTMENTS } from '../../auth/permissions';

/**
 * Each nav item has:
 *   key, label, path, icon, requires
 *
 * requires shapes:
 *   { roles: [...] }                         — visible if user has any role
 *   { roles: [...], depts: [...] }           — both must match (SUPERADMIN bypasses depts)
 *   { anyOf: [requires, requires, ...] }     — visible if any branch matches
 *
 * Backend remains the source of truth — frontend only hides items.
 */
export const navSections = [
  {
    key: 'overview',
    label: 'Overview',
    items: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        path: '/finance/dashboard',
        icon: FiHome,
        requires: { roles: [ROLES.USER, ROLES.ADMIN, ROLES.SUPERADMIN] },
      },
    ],
  },
  {
    key: 'finance',
    label: 'Finance',
    items: [
      {
        key: 'assets',
        label: 'Assets',
        path: '/finance/assets',
        icon: FiBox,
        requires: { roles: [ROLES.USER, ROLES.ADMIN, ROLES.SUPERADMIN] },
      },
      {
        key: 'expenses',
        label: 'Expenses',
        path: '/finance/expenses',
        icon: FiCreditCard,
        requires: { roles: [ROLES.USER, ROLES.ADMIN, ROLES.SUPERADMIN] },
      },
      {
        key: 'reimbursements',
        label: 'Reimbursements',
        path: '/finance/reimbursements',
        icon: FiRepeat,
        requires: { roles: [ROLES.USER, ROLES.ADMIN, ROLES.SUPERADMIN] },
      },
      {
        key: 'budgets',
        label: 'Budgets',
        path: '/finance/budgets',
        icon: FiPieChart,
        requires: {
          anyOf: [
            { roles: [ROLES.SUPERADMIN] },
            { roles: [ROLES.ADMIN], depts: [DEPARTMENTS.FINANCE] },
          ],
        },
      },
      {
        key: 'invoices',
        label: 'Invoices',
        path: '/finance/invoices',
        icon: FiFileText,
        requires: { roles: [ROLES.ADMIN, ROLES.SUPERADMIN] },
      },
      {
        key: 'customers',
        label: 'Customers',
        path: '/finance/customers',
        icon: FiUsers,
        requires: { roles: [ROLES.USER, ROLES.ADMIN, ROLES.SUPERADMIN] },
      },
      {
        key: 'subscriptions',
        label: 'Subscriptions',
        path: '/finance/subscriptions',
        icon: FiBriefcase,
        requires: { roles: [ROLES.ADMIN, ROLES.SUPERADMIN] },
      },
      {
        key: 'payments',
        label: 'Payments',
        path: '/finance/payments',
        icon: FiDollarSign,
        requires: {
          anyOf: [
            { roles: [ROLES.SUPERADMIN] },
            { roles: [ROLES.ADMIN], depts: [DEPARTMENTS.FINANCE] },
          ],
        },
      },
      {
        key: 'procurement',
        label: 'Procurement',
        path: '/finance/procurement',
        icon: FiShoppingCart,
        requires: { roles: [ROLES.USER, ROLES.ADMIN, ROLES.SUPERADMIN] },
      },
      {
        key: 'revenue',
        label: 'Revenue',
        path: '/finance/revenue',
        icon: FiTrendingUp,
        requires: { roles: [ROLES.SUPERADMIN] },
      },
    ],
  },
  {
    key: 'workflow',
    label: 'Workflow',
    items: [
      {
        key: 'approvals',
        label: 'Approvals Inbox',
        path: '/finance/approvals',
        icon: FiInbox,
        requires: { roles: [ROLES.USER, ROLES.ADMIN, ROLES.SUPERADMIN] },
      },
    ],
  },
  {
    key: 'admin',
    label: 'Administration',
    items: [
      {
        key: 'admin-users',
        label: 'Admin Users',
        path: '/admin/users',
        icon: FiUsers,
        requires: { roles: [ROLES.SUPERADMIN] },
      },
      {
        key: 'admin-leaves',
        label: 'Leave Management',
        path: '/admin/leaves',
        icon: FiCalendar,
        requires: { roles: [ROLES.SUPERADMIN] },
      },
      {
        key: 'admin-holidays',
        label: 'Manage Holidays',
        path: '/admin/holidays',
        icon: FiCalendar,
        requires: { roles: [ROLES.SUPERADMIN] },
      },
      {
        key: 'admin-departments',
        label: 'Department Access',
        path: '/admin/departments',
        icon: FiUserCheck,
        requires: { roles: [ROLES.SUPERADMIN] },
      },
      {
        key: 'admin-onboarding-docs',
        label: 'Manage Onboarding Docs',
        path: '/admin/onboarding-docs',
        icon: FiShield,
        requires: { roles: [ROLES.SUPERADMIN] },
      },
    ],
  },
];

export function filterNavForUser(can) {
  return navSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => can(item.requires)),
    }))
    .filter((section) => section.items.length > 0);
}
